/**
 * Formatting utilities
 * Centralizes formatting functions for consistent display across the application
 */

/**
 * Format a price amount (in centimes) to a localized currency string
 *
 * @param amount - Price amount in centimes (e.g., 2900 for 29.00€)
 * @param currency - Currency code (default: "EUR")
 * @param locale - Locale string (default: "fr-FR")
 * @returns Formatted price string (e.g., "29,00 €")
 */
export function formatPrice(amount: number, currency: string = "EUR", locale: string = "fr-FR"): string {
	if (isNaN(amount)) {
		return "0,00 €";
	}
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency: currency,
	}).format(amount / 100);
}

/**
 * Parse a date value (string or Date) to a Date object with validation
 * Centralizes date conversion logic to avoid repetitive patterns
 *
 * @param dateValue - Date string or Date object
 * @returns Parsed Date object or null if invalid
 */
export function parseDate(dateValue: string | Date | null | undefined): Date | null {
	if (!dateValue) {
		return null;
	}
	const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
	if (isNaN(date.getTime())) {
		return null;
	}
	return date;
}

/**
 * Format a date string to a localized date string
 *
 * @param dateValue - Date string or Date object
 * @param locale - Locale string (default: "fr-FR")
 * @param options - Intl.DateTimeFormatOptions (default: { day: "2-digit", month: "2-digit", year: "numeric" })
 * @returns Formatted date string (e.g., "01/01/2024")
 */
export function formatDate(
	dateValue: string | Date | null | undefined,
	locale: string = "fr-FR",
	options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" },
): string {
	const date = parseDate(dateValue);
	if (!date) {
		return "";
	}
	return new Intl.DateTimeFormat(locale, options).format(date);
}

/**
 * Format a date to a month string (e.g., "janvier 2024")
 *
 * @param dateValue - Date string or Date object
 * @param locale - Locale string (default: "fr-FR")
 * @returns Formatted month string (e.g., "janvier 2024")
 */
export function formatMonthString(dateValue: string | Date | null | undefined, locale: string = "fr-FR"): string {
	const date = parseDate(dateValue);
	if (!date) {
		return "";
	}
	return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date);
}

/**
 * Formats a float number with 2 decimal places, using comma as decimal separator.
 * Used for displaying prices without currency symbol.
 * @param value The number to format.
 * @returns The formatted string (e.g., "29,99").
 */
export function formatFloat(value: number): string {
	return value.toFixed(2).replace(".", ",");
}

/**
 * Truncate a string to a specified length with ellipsis
 *
 * @param str - String to truncate
 * @param maxLength - Maximum length before truncation (default: 16)
 * @returns Truncated string (e.g., "Long text here..." if maxLength is 16)
 */
export function truncateString(str: string | null | undefined, maxLength: number = 16): string {
	if (!str || typeof str !== "string") {
		return "";
	}
	if (str.length <= maxLength) {
		return str;
	}
	return `${str.substring(0, maxLength)}...`;
}

/**
 * Strips common legal prefixes from raw office names for compact display and consistent alphabetical sort in the office selector.
 */
export function formatOfficeSelectorDisplayName(raw: string): string {
	return raw
		.replace(/^SEL\s+/i, "")
		.replace(/^SCP\s+/i, "")
		.replace(/^SARL\s+/i, "")
		.replace(/^Office notarial de\s+/i, "")
		.replace(/^Office notarial\s+/i, "")
		.trim() || raw;
}

/**
 * Normalize email for search/comparison (trim and lowercase)
 * Used for consistent email handling across the application
 *
 * @param email - Email string to normalize
 * @returns Normalized email (trimmed and lowercased) or empty string if invalid
 */
export function normalizeEmail(email: string | null | undefined): string {
	if (!email || typeof email !== "string") {
		return "";
	}
	return email.trim().toLowerCase();
}

/**
 * Chaîne trimée non vide, sinon `undefined` (pour champs optionnels API / mapping).
 * Pour comparaisons / clés avec chaîne vide comme « absent », utiliser {@link trimToEmpty}.
 */
export function trimToUndefined(value: string | null | undefined): string | undefined {
	if (value == null) {
		return undefined;
	}
	const t = value.trim();
	return t.length > 0 ? t : undefined;
}

/**
 * `null` / `undefined` → `""` ; sinon `trim()` (y compris chaîne uniquement blanche → `""`).
 * Comparaisons (`===` IdNot, UID), clés de map, et anciens motifs `(x ?? "").trim()` / `x?.trim() ?? ""`.
 */
export function trimToEmpty(value: string | null | undefined): string {
	if (value == null) {
		return "";
	}
	return value.trim();
}

/**
 * Format splash notice message for display.
 * Normalizes literal \n (from env) to actual newlines, trims whitespace.
 * Ensures consistent rendering in modal (pre-wrap, no layout break).
 *
 * @param message - Raw message from NEXT_PUBLIC_SPLASH_MESSAGE or system_configuration
 * @returns Formatted message safe for display
 */
export function formatSplashMessage(message: string | null | undefined): string {
	if (!message || typeof message !== "string") {
		return "";
	}
	return message.replace(/\\n/g, "\n").trim();
}

function stripSpacesAndDots(phone: string): string {
	return phone.replace(/\s/g, "").replace(/\./g, "");
}

function toFrenchInternational(rawPhone: string): string | null {
	if (rawPhone.startsWith("0") && rawPhone.length === 10) return `+33${rawPhone.substring(1)}`;
	if (rawPhone.startsWith("33") && rawPhone.length === 11) return `+${rawPhone}`;
	if (rawPhone.length === 10 && /^0\d{9}$/.test(rawPhone)) return `+33${rawPhone.substring(1)}`;
	return null;
}

/**
 * Normalize phone number to international format (+33...)
 * Handles French phone numbers: 0X XX XX XX XX, 33X XX XX XX XX, +33X XX XX XX XX
 *
 * @param phone - Phone number to normalize
 * @returns Normalized phone number in international format or empty string if invalid
 */
export function normalizePhone(phone: string | null | undefined): string {
	if (!phone || typeof phone !== "string") return "";
	const rawPhone = stripSpacesAndDots(phone);
	if (!rawPhone) return "";
	if (rawPhone.startsWith("+")) return rawPhone;
	const normalized = toFrenchInternational(rawPhone);
	return normalized ?? rawPhone;
}
